import { instancePath } from "./instance";
import { createApi, unwrap, ApiError } from "@tern/api-client";
import type { Me, WorkspaceRole } from "@tern/api-client";
import { QueryClient, queryOptions } from "@tanstack/react-query";
import i18n from "@/i18n";

export const api = createApi(instancePath);
export { ApiError };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    },
  },
});

/** Translate a server error code, falling back to the server's English message. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "completion.child_approval") return i18n.t("completion.childApproval", { name: err.message });
    const key = `errors.${err.code}`;
    return i18n.exists(key) ? i18n.t(key) : err.message;
  }
  return i18n.t("errors.common.internal");
}

// ---- Queries ---------------------------------------------------------------

export const meQuery = queryOptions({
  queryKey: ["me"],
  queryFn: async (): Promise<Me | null> => {
    const res = await api.GET("/api/me");
    if (res.response.status === 401) return null;
    return unwrap(res);
  },
  staleTime: 60_000,
});

export const workspaceMembersQuery = (slug: string) =>
  queryOptions({
    queryKey: ["workspace", slug, "members"],
    queryFn: async () => unwrap(await api.GET("/api/workspaces/{slug}/members", { params: { path: { slug } } })),
  });

export const workspaceInvitationsQuery = (slug: string) =>
  queryOptions({
    queryKey: ["workspace", slug, "invitations"],
    queryFn: async () => unwrap(await api.GET("/api/workspaces/{slug}/invitations", { params: { path: { slug } } })),
  });

export const invitationPreviewQuery = (token: string) =>
  queryOptions({
    queryKey: ["invitation", token],
    queryFn: async () => unwrap(await api.GET("/api/invitations/{token}", { params: { path: { token } } })),
    retry: false,
  });

// ---- Mutations (plain functions; wrap with useMutation at the call site) ----

export const requestMagicLink = async (email: string, redirect_to?: string) =>
  unwrap(await api.POST("/api/auth/magic-link", { body: { email, redirect_to } }));

export const logout = async () => {
  const { useViews } = await import("./views");
  await useViews.getState().flush();
  const result = unwrap(await api.POST("/api/auth/logout"));
  useViews.getState().reset();
  return result;
};

export const updateMe = async (body: { name?: string; locale?: string; theme?: string; email_notifications?: boolean }) =>
  unwrap(await api.PATCH("/api/me", { body }));

export const inviteMember = async (slug: string, email: string, role: WorkspaceRole) =>
  unwrap(await api.POST("/api/workspaces/{slug}/invitations", { params: { path: { slug } }, body: { email, role } }));

export const revokeInvitation = async (slug: string, id: string) =>
  unwrap(await api.DELETE("/api/workspaces/{slug}/invitations/{id}", { params: { path: { slug, id } } }));

export const setMemberRole = async (slug: string, user_id: string, role: WorkspaceRole) =>
  unwrap(await api.PATCH("/api/workspaces/{slug}/members/{user_id}", { params: { path: { slug, user_id } }, body: { role } }));

export const removeMember = async (slug: string, user_id: string) =>
  unwrap(await api.DELETE("/api/workspaces/{slug}/members/{user_id}", { params: { path: { slug, user_id } } }));

export const acceptInvitation = async (token: string) =>
  unwrap(await api.POST("/api/invitations/{token}/accept", { params: { path: { token } } }));
